import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { callRoutingEndpoint } from '../../services/api/endpoints/call-routing.endpoint';
import {
  CreateRoutingConfigPayload,
  CreateRoutingRulePayload,
  RoutingConfig,
  RoutingRule,
  UpdateRoutingConfigPayload,
  UpdateRoutingRulePayload,
} from '../../types/phone-numbers';

interface CallRoutingState {
  routingConfig: RoutingConfig | null;
  routingRules: RoutingRule[];
  isLoadingConfig: boolean;
  isLoadingRules: boolean;
  isSavingConfig: boolean;
  isSavingRule: boolean;
  error: string | null;
}

const initialState: CallRoutingState = {
  routingConfig: null,
  routingRules: [],
  isLoadingConfig: false,
  isLoadingRules: false,
  isSavingConfig: false,
  isSavingRule: false,
  error: null,
};

export const fetchRoutingConfig = createAsyncThunk(
  'callRouting/fetchConfig',
  async (
    { organisationId, phoneNumberId }: { organisationId: string; phoneNumberId: number },
    { rejectWithValue }
  ) => {
    try {
      return await callRoutingEndpoint.getConfig(organisationId, phoneNumberId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch routing config');
    }
  }
);

export const createRoutingConfig = createAsyncThunk(
  'callRouting/createConfig',
  async (
    {
      organisationId,
      phoneNumberId,
      payload,
    }: { organisationId: string; phoneNumberId: number; payload: CreateRoutingConfigPayload },
    { rejectWithValue }
  ) => {
    try {
      return await callRoutingEndpoint.createConfig(organisationId, phoneNumberId, payload);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create routing config');
    }
  }
);

export const updateRoutingConfig = createAsyncThunk(
  'callRouting/updateConfig',
  async (
    {
      organisationId,
      phoneNumberId,
      payload,
    }: { organisationId: string; phoneNumberId: number; payload: UpdateRoutingConfigPayload },
    { rejectWithValue }
  ) => {
    try {
      return await callRoutingEndpoint.updateConfig(organisationId, phoneNumberId, payload);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update routing config');
    }
  }
);

export const deleteRoutingConfig = createAsyncThunk(
  'callRouting/deleteConfig',
  async (
    { organisationId, phoneNumberId }: { organisationId: string; phoneNumberId: number },
    { rejectWithValue }
  ) => {
    try {
      await callRoutingEndpoint.deleteConfig(organisationId, phoneNumberId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to delete routing config');
    }
  }
);

export const fetchRoutingRules = createAsyncThunk(
  'callRouting/fetchRules',
  async (
    { organisationId, phoneNumberId }: { organisationId: string; phoneNumberId: number },
    { rejectWithValue }
  ) => {
    try {
      return await callRoutingEndpoint.listRules(organisationId, phoneNumberId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch routing rules');
    }
  }
);

export const createRoutingRule = createAsyncThunk(
  'callRouting/createRule',
  async (
    {
      organisationId,
      phoneNumberId,
      payload,
    }: { organisationId: string; phoneNumberId: number; payload: CreateRoutingRulePayload },
    { rejectWithValue }
  ) => {
    try {
      return await callRoutingEndpoint.createRule(organisationId, phoneNumberId, payload);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create routing rule');
    }
  }
);

export const updateRoutingRule = createAsyncThunk(
  'callRouting/updateRule',
  async (
    {
      organisationId,
      ruleId,
      payload,
    }: { organisationId: string; ruleId: number; payload: UpdateRoutingRulePayload },
    { rejectWithValue }
  ) => {
    try {
      return await callRoutingEndpoint.updateRule(organisationId, ruleId, payload);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update routing rule');
    }
  }
);

export const deleteRoutingRule = createAsyncThunk(
  'callRouting/deleteRule',
  async (
    { organisationId, ruleId }: { organisationId: string; ruleId: number },
    { rejectWithValue }
  ) => {
    try {
      await callRoutingEndpoint.deleteRule(organisationId, ruleId);
      return ruleId;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to delete routing rule');
    }
  }
);

export const triggerRoutingSync = createAsyncThunk(
  'callRouting/triggerSync',
  async (
    { organisationId, phoneNumberId }: { organisationId: string; phoneNumberId: number },
    { rejectWithValue }
  ) => {
    try {
      await callRoutingEndpoint.triggerSync(organisationId, phoneNumberId);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to trigger routing sync');
    }
  }
);

const callRoutingSlice = createSlice({
  name: 'callRouting',
  initialState,
  reducers: {
    clearCallRouting(state) {
      state.routingConfig = null;
      state.routingRules = [];
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRoutingConfig.pending, state => {
        state.isLoadingConfig = true;
        state.error = null;
      })
      .addCase(fetchRoutingConfig.fulfilled, (state, action) => {
        state.isLoadingConfig = false;
        // Backend returns empty object {} when no config exists — treat as null
        const payload = action.payload;
        state.routingConfig =
          payload && typeof payload === 'object' && Object.keys(payload).length > 0
            ? payload
            : null;
      })
      .addCase(fetchRoutingConfig.rejected, (state, action) => {
        state.isLoadingConfig = false;
        state.error = action.payload as string;
      })

      .addCase(createRoutingConfig.pending, state => {
        state.isSavingConfig = true;
        state.error = null;
      })
      .addCase(createRoutingConfig.fulfilled, (state, action) => {
        state.isSavingConfig = false;
        state.routingConfig = action.payload;
      })
      .addCase(createRoutingConfig.rejected, (state, action) => {
        state.isSavingConfig = false;
        state.error = action.payload as string;
      })

      .addCase(updateRoutingConfig.pending, state => {
        state.isSavingConfig = true;
        state.error = null;
      })
      .addCase(updateRoutingConfig.fulfilled, (state, action) => {
        state.isSavingConfig = false;
        state.routingConfig = action.payload;
      })
      .addCase(updateRoutingConfig.rejected, (state, action) => {
        state.isSavingConfig = false;
        state.error = action.payload as string;
      })

      .addCase(deleteRoutingConfig.fulfilled, state => {
        state.routingConfig = null;
      })
      .addCase(deleteRoutingConfig.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(fetchRoutingRules.pending, state => {
        state.isLoadingRules = true;
        state.error = null;
      })
      .addCase(fetchRoutingRules.fulfilled, (state, action) => {
        state.isLoadingRules = false;
        state.routingRules = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchRoutingRules.rejected, (state, action) => {
        state.isLoadingRules = false;
        state.error = action.payload as string;
      })

      .addCase(createRoutingRule.pending, state => {
        state.isSavingRule = true;
        state.error = null;
      })
      .addCase(createRoutingRule.fulfilled, (state, action) => {
        state.isSavingRule = false;
        state.routingRules.push(action.payload);
        state.routingRules.sort((a, b) => a.priority - b.priority);
      })
      .addCase(createRoutingRule.rejected, (state, action) => {
        state.isSavingRule = false;
        state.error = action.payload as string;
      })

      .addCase(updateRoutingRule.pending, state => {
        state.isSavingRule = true;
        state.error = null;
      })
      .addCase(updateRoutingRule.fulfilled, (state, action) => {
        state.isSavingRule = false;
        const index = state.routingRules.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.routingRules[index] = action.payload;
        }
        state.routingRules.sort((a, b) => a.priority - b.priority);
      })
      .addCase(updateRoutingRule.rejected, (state, action) => {
        state.isSavingRule = false;
        state.error = action.payload as string;
      })

      .addCase(deleteRoutingRule.fulfilled, (state, action) => {
        state.routingRules = state.routingRules.filter(r => r.id !== action.payload);
      })
      .addCase(deleteRoutingRule.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearCallRouting } = callRoutingSlice.actions;
export const selectCallRouting = (state: RootState) => state.callRouting;
export default callRoutingSlice.reducer;
